import { isAnonymousMarketplaceEndpoint } from '../auth/auth-guard';
// packages/web/src/lib/api/fetch-helper.ts
// apiFetch function for direct fetch calls

import { apiFetchWithCircuitBreaker } from './circuit-breaker';
import { withRetry } from './retry';
import { getApiBaseUrl } from '../../utils/apiBase';
import { getSubdomain, getTenantId, getToken } from '../auth/session-manager';
import {
  decodeSubdomainFromJwt,
  decodeTenantId,
  isLikelyJwt,
  isStubToken,
} from '../auth/token-manager';

// ─────────────────────────────
// E2E / Harness net tracing
// ─────────────────────────────
const TRACE_NET =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_E2E_NETLOGS === '1') ||
  (typeof process !== 'undefined' && process.env?.SMOKE_HARNESS_WEEK === 'Week 11');

function genCid() {
  try {
    return Math.random().toString(36).slice(2, 10);
  } catch {
    return String(Date.now());
  }
}
function netLog(stage: 'start' | 'end' | 'error', meta: Record<string, any>) {
  if (!TRACE_NET) return;
  try {
    console.error('[NET]', JSON.stringify({ stage, ...meta }));
  } catch {}
}

// Original implementation (now wrapped with circuit breaker)
async function apiFetchInternal(path: string, options: RequestInit = {}): Promise<any> {
  const headers = new Headers(options.headers || {});
  const cid = headers.get('X-CID') || genCid();
  const start = Date.now();
  const token = typeof window !== 'undefined' ? getToken() : null;
  let tenantId = typeof window !== 'undefined' ? getTenantId() : '';
  let subdomain = typeof window !== 'undefined' ? getSubdomain() : '';

  // LOGIN DEBUGGING: Log authentication-related API calls
  const isAuthRelated = path.includes('/auth/') || path.includes('/me') || path.includes('/cart');
  if (isAuthRelated) {
    console.log('[LOGIN_DEBUG][apiFetch] ===== API CALL START =====');
    console.log('[LOGIN_DEBUG][apiFetch] Path:', path);
    console.log('[LOGIN_DEBUG][apiFetch] Method:', options.method || 'GET');
    console.log('[LOGIN_DEBUG][apiFetch] Token present:', !!token);
    console.log(
      '[LOGIN_DEBUG][apiFetch] Token prefix:',
      token ? `${token.substring(0, 20)}...` : 'null',
    );
    console.log('[LOGIN_DEBUG][apiFetch] TenantId:', tenantId);
    console.log('[LOGIN_DEBUG][apiFetch] Subdomain:', subdomain);
  }

  // SPECIAL DEBUGGING FOR SERVICE CREATION
  if (path === '/services' && options.method === 'POST') {
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] ===== APIFETCH SERVICE CREATION DEBUG =====');
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Path:', path);
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Method:', options.method);
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Raw options.body:', options.body);
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] options.body type:', typeof options.body);

    if (options.body) {
      try {
        const parsedBody = JSON.parse(options.body as string);
        console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Parsed body:', parsedBody);
        console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Body keys:', Object.keys(parsedBody));
        console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Body.name field:', parsedBody.name);
        console.log('[APIFETCH][SERVICE_CREATION][DEBUG] Body.name type:', typeof parsedBody.name);
        console.log(
          '[APIFETCH][SERVICE_CREATION][DEBUG] Body.name length:',
          parsedBody.name?.length || 0,
        );
        console.log(
          '[APIFETCH][SERVICE_CREATION][DEBUG] Body.name trimmed:',
          parsedBody.name?.trim(),
        );
        console.log(
          '[APIFETCH][SERVICE_CREATION][DEBUG] Body.name trimmed length:',
          parsedBody.name?.trim()?.length || 0,
        );
      } catch (parseError) {
        console.error('[APIFETCH][SERVICE_CREATION][DEBUG] Failed to parse body:', parseError);
      }
    }
    console.log('[APIFETCH][SERVICE_CREATION][DEBUG] ===== END APIFETCH DEBUG =====');
  }

  // Never attach stub tokens; prefer real JWTs only
  // Skip authorization for anonymous marketplace browsing endpoints
  const anonymousMarketplaceEndpoint = isAnonymousMarketplaceEndpoint(path, options.method || 'GET');
  if (
    token &&
    !isStubToken(token) &&
    isLikelyJwt(token) &&
    !headers.has('Authorization') &&
    !anonymousMarketplaceEndpoint
  ) {
    headers.set('Authorization', `Bearer ${token}`);
    if (isAuthRelated) {
      console.log('[LOGIN_DEBUG][apiFetch] Attached Authorization header');
    }
  } else if (isAuthRelated) {
    console.log('[LOGIN_DEBUG][apiFetch] Skipped Authorization header:', {
      hasToken: !!token,
      isStubToken: token ? isStubToken(token) : false,
      isLikelyJwt: token ? isLikelyJwt(token) : false,
      hasExistingAuth: headers.has('Authorization'),
      isAnonymousEndpoint: anonymousMarketplaceEndpoint,
    });
  }
  // If storage is empty, lift values off the JWT (common right after login on apex host)
  if (!tenantId) {
    const tid = decodeTenantId(token);
    if (tid) tenantId = tid;
  }
  // Prefer the JWT subdomain claim to avoid header/JWT drift (even if storage has a value)
  const fromJwt = decodeSubdomainFromJwt(token);
  if (fromJwt) subdomain = fromJwt;
  // Prefer UUID header; only send subdomain if we do NOT have a UUID
  if (!headers.has('X-Tenant-Id') && !headers.has('X-Subdomain')) {
    if (tenantId) {
      headers.set('X-Tenant-Id', tenantId);
    } else if (subdomain) {
      headers.set('X-Subdomain', subdomain);
    }
  }
  if (process.env.SMOKE_HARNESS_WEEK === 'Week 11' && !headers.has('x-smoke-harness')) {
    headers.set('x-smoke-harness', 'true');
  }
  if (TRACE_NET && !headers.has('X-E2E-TRACE')) headers.set('X-E2E-TRACE', '1');
  headers.set('X-CID', cid);
  // Don't set Content-Type for FormData - let the browser set it with boundary
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  // Use the same runtime-safe resolver the axios instance uses
  const base = getApiBaseUrl(); // Always ends with /api, no trailing slash
  let p = path || '';
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.startsWith('/api/')) p = p.replace(/^\/api\//, '/');

  const url = `${base}${p}`;
  netLog('start', { cid, method: (options.method || 'GET').toString().toUpperCase(), url: p });

  // Use retry logic for API calls
  const res = await withRetry(
    async () => {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // Throw error for non-ok responses so they can be retried
      if (!response.ok) {
        // Read the response body for error details (e.g., validation messages)
        let errorBody = '';
        try {
          const cloned = response.clone();
          errorBody = await cloned.text();
        } catch {
          /* ignore read errors */
        }
        const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.response = response;
        error.responseBody = errorBody;
        throw error;
      }

      return response;
    },
    {
      maxRetries: 2, // Limit retries for API calls to avoid overwhelming servers
      baseDelay: 500,
      maxDelay: 5000,
      retryCondition: (error) => {
        // Don't retry authentication errors (401/403)
        if (error.status && (error.status === 401 || error.status === 403)) {
          console.log(`[APIFETCH] Authentication error ${error.status} - not retrying`);
          return false;
        }
        // Don't retry client errors (4xx) except auth errors
        if (error.status && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry server errors (5xx) and network errors
        return true;
      },
      onRetry: (error, attempt) => {
        console.log(
          `[APIFETCH RETRY] Retrying ${options.method || 'GET'} ${p} (attempt ${attempt + 1})`,
        );
      },
    },
  );

  // FAANG-GRADE: Handle authentication failures gracefully - but only for actual auth errors
  if (res.status === 401 || res.status === 403) {
    console.warn(
      `[APIFETCH] Authentication failed (${res.status}) for ${options.method || 'GET'} ${p}`,
    );

    // Before clearing auth data, verify this is actually an authentication issue
    // Check if the response body indicates an auth problem vs a server error
    let responseBody: any = null;
    let isAuthError = false;

    try {
      // Clone the response to read the body without consuming it
      const responseClone = res.clone();
      const contentType = responseClone.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        responseBody = await responseClone.json();
      } else {
        responseBody = await responseClone.text();
      }

      // Only consider it an auth error if we can clearly identify auth-related content
      isAuthError =
        typeof responseBody === 'object' &&
        responseBody !== null &&
        // Check for explicit auth error messages
        ((responseBody.error &&
          (responseBody.error.toLowerCase().includes('token') ||
            responseBody.error.toLowerCase().includes('auth') ||
            responseBody.error.toLowerCase().includes('unauthorized') ||
            responseBody.error.toLowerCase().includes('forbidden') ||
            responseBody.error.toLowerCase().includes('expired') ||
            responseBody.error.toLowerCase().includes('invalid'))) ||
          (responseBody.message &&
            (responseBody.message.toLowerCase().includes('token') ||
              responseBody.message.toLowerCase().includes('auth') ||
              responseBody.message.toLowerCase().includes('unauthorized') ||
              responseBody.message.toLowerCase().includes('forbidden') ||
              responseBody.message.toLowerCase().includes('expired') ||
              responseBody.message.toLowerCase().includes('invalid'))) ||
          // Check for auth-related status codes in the response
          responseBody.code === 'AUTHENTICATION_ERROR' ||
          responseBody.code === 'TOKEN_EXPIRED' ||
          responseBody.code === 'INVALID_TOKEN');

      // Additional check: if response has www-authenticate header, it's definitely auth
      if (res.headers.get('www-authenticate')) {
        isAuthError = true;
      }
    } catch (bodyError) {
      console.warn(`[APIFETCH] Could not read response body for auth verification:`, bodyError);
      // If we can't read the body, be conservative and don't clear auth data
      isAuthError = false;
    }

    if (isAuthError) {
      console.log(`[APIFETCH] Confirmed authentication error - clearing auth data`);

      // Clear invalid tokens
      try {
        const { clearAllAuthData } = await import('../auth/session-manager');
        clearAllAuthData();
        console.log(`[APIFETCH] Cleared invalid authentication data`);
      } catch (clearError) {
        console.warn(`[APIFETCH] Failed to clear auth data:`, clearError);
      }

      // For authenticated endpoints, redirect to login if not already there
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        console.log(`[APIFETCH] Redirecting to login due to authentication failure`);
        window.location.href = '/login';
        // Return a promise that never resolves to prevent further execution
        return new Promise(() => {});
      }
    } else {
      console.log(
        `[APIFETCH] ${res.status} response does not appear to be an authentication error - not clearing auth data`,
      );
      console.log(`[APIFETCH] Response body:`, responseBody);
      // This might be a server error (500) that returned 401/403 incorrectly, or a business logic error
      // Don't clear auth data, just throw the error for component-level handling
    }

    // Throw error for component-level handling
    const error: any = new Error(`Authentication failed: ${res.status}`);
    error.status = res.status;
    error.response = res;
    throw error;
  }

  // SPECIAL DEBUGGING FOR SERVICE CREATION RESPONSE
  if (path === '/services' && options.method === 'POST') {
    console.log(
      '[APIFETCH][SERVICE_CREATION][RESPONSE][DEBUG] ===== APIFETCH RESPONSE DEBUG =====',
    );
    console.log('[APIFETCH][SERVICE_CREATION][RESPONSE][DEBUG] Response status:', res.status);
    console.log('[APIFETCH][SERVICE_CREATION][RESPONSE][DEBUG] Response ok:', res.ok);
    console.log('[APIFETCH][SERVICE_CREATION][RESPONSE][DEBUG] Response headers:', res.headers);
  }

  // Response is already validated by retry logic - it's ok at this point
  netLog('end', {
    cid,
    method: (options.method || 'GET').toString().toUpperCase(),
    url: p,
    status: res.status,
    durationMs: Date.now() - start,
  });

  // LOGIN DEBUGGING: Log authentication-related API success
  if (isAuthRelated) {
    console.log('[LOGIN_DEBUG][apiFetch] ===== API CALL SUCCESS =====');
    console.log('[LOGIN_DEBUG][apiFetch] Status:', res.status);
    console.log('[LOGIN_DEBUG][apiFetch] Duration:', Date.now() - start, 'ms');
    console.log('[LOGIN_DEBUG][apiFetch] ===== END API CALL SUCCESS =====');
  }

  // SPECIAL DEBUGGING FOR SERVICE CREATION SUCCESS RESPONSE
  if (path === '/services' && options.method === 'POST') {
    console.log('[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] ===== APIFETCH SUCCESS DEBUG =====');
    console.log('[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Response status:', res.status);
    console.log(
      '[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Content-Type:',
      res.headers.get('content-type'),
    );
    try {
      const responseClone = res.clone();
      const responseText = await responseClone.text();
      console.log('[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Raw response text:', responseText);
      if (responseText) {
        try {
          const responseJson = JSON.parse(responseText);
          console.log(
            '[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Parsed response JSON:',
            responseJson,
          );
        } catch (parseError) {
          console.log('[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Response is not JSON');
        }
      }
    } catch (cloneError) {
      console.error(
        '[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] Error reading response:',
        cloneError,
      );
    }
    console.log('[APIFETCH][SERVICE_CREATION][SUCCESS][DEBUG] ===== END SUCCESS DEBUG =====');
  }

  const ct = res.headers.get('content-type') || '';
  const result = ct.includes('application/json') ? res.json() : res.text();

  // LOGIN DEBUGGING: Log response data for auth calls
  if (isAuthRelated) {
    result
      .then((data) => {
        console.log('[LOGIN_DEBUG][apiFetch] Response data:', {
          hasData: !!data,
          dataType: typeof data,
          dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'N/A',
          dataLength: data && typeof data === 'string' ? data.length : 'N/A',
        });
      })
      .catch((err) => {
        console.log('[LOGIN_DEBUG][apiFetch] Error reading response data:', err);
      });
  }

  return result;
}

// Runtime API endpoint validation for development
function validateApiEndpoint(path: string): void {
  // Only validate in development mode
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  // Skip validation for non-API paths
  if (!path.startsWith('/api/')) {
    return;
  }

  // Import API endpoints dynamically to avoid circular dependencies
  let VALID_ENDPOINTS: Set<string>;
  try {
    // Use dynamic import to avoid bundling issues
    const endpointsModule = require('./endpoints');
    const allEndpoints = Object.values(endpointsModule.API_ENDPOINTS)
      .flatMap((group: any) => Object.values(group as any))
      .filter((endpoint): endpoint is string => typeof endpoint === 'string');
    VALID_ENDPOINTS = new Set(allEndpoints);
  } catch (error) {
    // If we can't load endpoints, skip validation but log warning
    console.warn('[API_VALIDATION] Could not load API_ENDPOINTS for validation:', error);
    return;
  }

  // Check if endpoint is valid
  if (!VALID_ENDPOINTS.has(path)) {
    const suggestion = suggestCorrectEndpoint(path, VALID_ENDPOINTS);

    // Phase 1: Just warn (weeks 1-2 migration period)
    // Phase 2: Throw error (weeks 3-4 strict enforcement)
    const shouldThrow = process.env.API_STRICT_MODE === 'true';

    const message = `🚨 POTENTIAL INVALID API ENDPOINT: ${path}
Valid endpoints: ${Array.from(VALID_ENDPOINTS).slice(0, 5).join(', ')}${VALID_ENDPOINTS.size > 5 ? '...' : ''}
${suggestion ? `💡 Did you mean: ${suggestion}?` : ''}
Add to API_ENDPOINTS in packages/web/src/lib/api/endpoints.ts
Stack trace: ${new Error().stack}`;

    if (shouldThrow) {
      throw new Error(`Invalid API endpoint: ${path}. Use ApiClient methods instead.`);
    } else {
      console.warn(message);
    }
  }
}

// Helper function to suggest correct endpoints
function suggestCorrectEndpoint(path: string, validEndpoints: Set<string>): string | null {
  // Common corrections for frequent mistakes
  const corrections: Record<string, string> = {
    '/api/banking/balance': '/api/partners/banking/balance',
    '/api/partners/balance': '/api/partners/banking/balance',
    '/api/partners/deposits': '/api/partners/perks/deposits',
    '/api/commission/projections': '/api/commission/projections',
    '/api/partners/perks/balance': '/api/partners/banking/balance',
  };

  if (corrections[path]) {
    return corrections[path];
  }

  // Fuzzy matching based on path segments
  const pathParts = path.split('/').filter(Boolean);
  for (const validPath of validEndpoints) {
    const validParts = validPath.split('/').filter(Boolean);
    // Check if most path segments match
    const matchingParts = pathParts.filter((part, index) => validParts[index] === part).length;
    if (matchingParts >= Math.max(2, pathParts.length - 1)) {
      return validPath;
    }
  }

  return null;
}

// Backward-compatible fetch helper for existing pages that import { apiFetch }
export async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {
  // Validate API endpoint in development
  validateApiEndpoint(path);

  // Use circuit breaker protection for API safety
  return apiFetchWithCircuitBreaker(path, options, undefined, async () => {
    return apiFetchInternal(path, options);
  });
}
